import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { API_URLS } from "./apis/urls";
import Api from "./utils/apiCall";

export interface INocHigherEducationRequest {
  id: number;
  referenceNumber: string;
  empId: string;
  approverUserID: string | null;
  approverName: string | null;
  status: string | null;
  requestStatus: string | null;
  courseName: string;
  institute: string;
  diplomaOrDegree: string;
  partTimeOrCorresp: string;
  requestCreationDate: string | null;
  created_at: string;
  remarks: string;
  hRemarks: string | null;
  is_draft: boolean;
  course_type?: {
    course_type_id: string;
    course_type_title: string;
  } | null;
}

interface INocHigherEducationState {
  requests: INocHigherEducationRequest[];
  currentRequest: INocHigherEducationRequest | null;
  loading: boolean;
  error: string | null;
  createLoading: boolean;
  deleteLoading: boolean;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const initialState: INocHigherEducationState = {
  requests: [],
  currentRequest: null,
  loading: false,
  error: null,
  createLoading: false,
  deleteLoading: false,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
};

// Fetch all requests
export const fetchNOCHigherEducationRequests = createAsyncThunk(
  "nocHigherEducation/fetchAll",
  async (payload: {
    query?: string;
    page?: number;
    limit?: number;
    orderColumn?: string;
    orderBy?: string;
    requestStatus?: string;
    startDate?: string;
    endDate?: string;
  }, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.NOC.FIND_ALL_HIGHER_EDUCATION,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Fetch single request
export const fetchNOCHigherEducationRequest = createAsyncThunk(
  "nocHigherEducation/fetchOne",
  async (payload: { id: number }, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.NOC.FIND_ONE_HIGHER_EDUCATION,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Create or update request
export const createOrUpdateNOCHigherEducation = createAsyncThunk(
  "nocHigherEducation/createOrUpdate",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.NOC.CREATE_OR_UPDATE_HIGHER_EDUCATION,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Delete request
export const deleteNOCHigherEducationRequest = createAsyncThunk(
  "nocHigherEducation/delete",
  async (payload: { id: number }, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.NOC.DELETE_HIGHER_EDUCATION,
        payload
      );
      return { ...response.data, deletedId: payload.id };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Approve/Reject action
export const approveRejectNOCHigherEducation = createAsyncThunk(
  "nocHigherEducation/approveReject",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.NOC.APPROVE_REJECT_HIGHER_EDUCATION,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

const nocHigherEducationSlice = createSlice({
  name: "nocHigherEducation",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetNocHigherEducationState: (state) => {
      Object.assign(state, initialState);
    },
    setCurrentRequest: (state, action: PayloadAction<number>) => {
      const requestId = action.payload;
      const request = state.requests.find((req) => req.id === requestId);
      state.currentRequest = request || null;
    },
    clearCurrentRequest: (state) => {
      state.currentRequest = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all requests
      .addCase(fetchNOCHigherEducationRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNOCHigherEducationRequests.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload.data || {};
        state.requests = responseData.data || [];
        state.pagination = {
          page: responseData.page || 1,
          limit: responseData.limit || 10,
          total: responseData.total || 0,
          totalPages: responseData.totalPages || 0,
        };
      })
      .addCase(fetchNOCHigherEducationRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.requests = [];
      })
      // Fetch single request
      .addCase(fetchNOCHigherEducationRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNOCHigherEducationRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.currentRequest = action.payload.data || null;
      })
      .addCase(fetchNOCHigherEducationRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create or update
      .addCase(createOrUpdateNOCHigherEducation.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createOrUpdateNOCHigherEducation.fulfilled, (state) => {
        state.createLoading = false;
      })
      .addCase(createOrUpdateNOCHigherEducation.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload as string;
      })
      // Delete request
      .addCase(deleteNOCHigherEducationRequest.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteNOCHigherEducationRequest.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.requests = state.requests.filter(
          (req) => req.id !== action.payload.deletedId
        );
        state.pagination.total = Math.max(0, state.pagination.total - 1);
      })
      .addCase(deleteNOCHigherEducationRequest.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload as string;
      })
      // Approve/Reject
      .addCase(approveRejectNOCHigherEducation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveRejectNOCHigherEducation.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(approveRejectNOCHigherEducation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  resetNocHigherEducationState,
  setCurrentRequest,
  clearCurrentRequest,
} = nocHigherEducationSlice.actions;
export default nocHigherEducationSlice.reducer;
